import { Col, DatePicker, Form, Row, Select, Space } from 'antd';
import * as React from 'react';
import { memo, useEffect, useRef, useState } from 'react';
import { getArticleNames, getCategoriesApi } from '../../actions';
import { ANIMATION_DURATION, langLabels, languages } from '../../admin-knowledge-base.constants';
import { Article, Category, FormProps, Statuses } from '../../admin-knowledge-base.type';
import { animateForm, disabledDate } from '../../admin-knowledge-base.utils';
import ContentField from './content-field';
import LinkedArticlesSelect from './linked-articles-select';
import TitleField from './title-field';
import Button from '/react4xp/mixins/ui/button/button';
import { Languages } from '/react4xp/parts/knowledge-base-common/knowledge-base-common.type';

export const ArticleForm = memo(
    ({ onSubmit, close, form, id, tags, sections }: FormProps<Article>) => {
        const [activeLanguage, setActiveLanguage] = useState<Languages>(languages[0]);
        const [categories, setCategories] = useState<Category[]>([]);
        const localizedFieldsRef = useRef<HTMLDivElement>(null);

        const activeSection = Form.useWatch('sectionId', form);
        const effective = Form.useWatch(['status', 'effective'], form);

        const handleLanguageChange = (language: Languages) => {
            setActiveLanguage(language);
            animateForm(localizedFieldsRef.current);
        };

        useEffect(() => {
            setTimeout(() => {
                form.setFieldValue(
                    ['draft', 'title'],
                    form.getFieldValue([activeLanguage, 'title'])
                );
                form.setFieldValue(
                    ['draft', 'content'],
                    form.getFieldValue([activeLanguage, 'content'])
                );
            }, ANIMATION_DURATION / 3);
        }, [activeLanguage]);

        useEffect(() => {
            if (id) {
                const [sectionId] = form.getFieldValue('path').split('/').slice(2);
                getCategoriesApi(sectionId).then(setCategories);
                form.setFieldValue('sectionId', sectionId);
            }
        }, [id]);

        useEffect(() => {
            getCategoriesApi(activeSection).then((categories) => {
                setCategories(categories);
                const activeCategoryPath = form.getFieldValue('parent');
                if (!categories.find(({ path }) => path === activeCategoryPath)) {
                    form.resetFields(['parent']);
                }
            });
        }, [activeSection]);

        const handlePublish = () => {
            if (effective) {
                form.setFieldValue(['status', 'current'], Statuses.DRAFT);
                form.setFieldValue(['status', 'planned'], Statuses.PUBLISH);
            } else {
                form.setFieldValue(['status', 'current'], Statuses.PUBLISH);
            }
        };

        const handleDraft = () => {
            form.setFieldValue(['status', 'current'], Statuses.DRAFT);
            form.setFieldValue(['status', 'effective'], null);
        };

        return (
            <Form
                form={form}
                onFinish={onSubmit}
                validateTrigger={['onBlur']}
            >
                <Form.Item
                    hidden
                    name="id"
                />
                <Form.Item
                    hidden
                    name={['status', 'current']}
                />
                <Form.Item
                    hidden
                    name={['status', 'planned']}
                />
                <Row gutter={16}>
                    <Col
                        xs={24}
                        lg={8}
                    >
                        <Form.Item
                            name="sectionId"
                            label="Раздел"
                            labelCol={{ span: 24 }}
                            rules={[{ required: true, message: 'Обязательное поле' }]}
                        >
                            <Select
                                options={sections.map((section) => ({
                                    value: section.id,
                                    label: section.ru.title,
                                }))}
                            />
                        </Form.Item>
                    </Col>
                    <Col
                        xs={24}
                        lg={8}
                    >
                        <Form.Item
                            name="parent"
                            label="Категория"
                            labelCol={{ span: 24 }}
                            rules={[{ required: true, message: 'Обязательное поле' }]}
                        >
                            <Select
                                options={categories.map((category) => ({
                                    value: category.path,
                                    label: category.ru.title,
                                }))}
                                disabled={!form.getFieldValue('sectionId')}
                            />
                        </Form.Item>
                    </Col>
                    <Col
                        xs={24}
                        lg={8}
                    >
                        <Form.Item
                            name="tagId"
                            label="Рубрика"
                            labelCol={{ span: 24 }}
                            rules={[{ required: true, message: 'Обязательное поле' }]}
                        >
                            <Select
                                options={tags.map((tag) => ({
                                    value: tag.id,
                                    label: tag.ru.title,
                                }))}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Space.Compact>
                    {languages.map((language) => (
                        <Button
                            key={language}
                            onClick={() => handleLanguageChange(language)}
                            color={language === activeLanguage ? 'gold' : ''}
                        >
                            {langLabels[language]}
                        </Button>
                    ))}
                </Space.Compact>
                <div ref={localizedFieldsRef}>
                    <TitleField
                        activeLanguage={activeLanguage}
                        form={form}
                        minLength={5}
                        maxLength={10000}
                    />
                    <ContentField
                        activeLanguage={activeLanguage}
                        form={form}
                        id={id}
                        minLength={5}
                    />
                </div>
                <Row
                    justify="start"
                    gutter={20}
                >
                    <Col
                        xs={24}
                        md={16}
                    >
                        <Form.Item
                            name="linked"
                            label="Связанные статьи"
                            labelCol={{ span: 24 }}
                            // getValueFromEvent={(event) => event?.map((item) => item.value)}
                            rules={[
                                {
                                    validator: (_, value) =>
                                        value?.length > 5
                                            ? Promise.reject(
                                                  'Максимальное количество связанных статей - 5'
                                              )
                                            : Promise.resolve(),
                                },
                            ]}
                        >
                            <LinkedArticlesSelect
                                mode="multiple"
                                placeholder="Искать в заголовках статей..."
                                fetchOptions={getArticleNames}
                            />
                        </Form.Item>
                    </Col>
                    <Col
                        xs={24}
                        md={8}
                    >
                        <Space direction="vertical">
                            <Form.Item
                                label="Запланировать публикацию"
                                name={['status', 'effective']}
                                labelCol={{ span: 24 }}
                            >
                                <DatePicker
                                    picker="date"
                                    disabledDate={disabledDate}
                                    format={'DD.MM.YYYY'}
                                />
                            </Form.Item>
                        </Space>
                    </Col>
                    <Col xs={24}>
                        <Space
                            style={{
                                flexWrap: 'wrap',
                            }}
                        >
                            <Form.Item>
                                <Button
                                    onClick={handlePublish}
                                    htmlType="submit"
                                    color="gold"
                                >
                                    {effective ? 'Запланировать' : 'Опубликовать'}
                                </Button>
                            </Form.Item>
                            <Form.Item>
                                <Button
                                    htmlType="submit"
                                    onClick={handleDraft}
                                >
                                    Сохранить черновик
                                </Button>
                            </Form.Item>
                            <Form.Item>
                                <Button onClick={close}>Отменить</Button>
                            </Form.Item>
                        </Space>
                    </Col>
                </Row>
            </Form>
        );
    }
);
